import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { FaMapMarkerAlt, FaClock, FaBriefcase, FaGraduationCap, FaArrowRight, FaCheckCircle } from 'react-icons/fa';
import Base from './Base';

function Careers() {
  const [selectedDepartment, setSelectedDepartment] = useState('all');

  const departments = [
    {
      id: 'cardiology',
      name: 'Cardiology Department',
      description: 'Join our cardiology team and make a difference in heart care',
      jobs: [
        {
          title: 'Senior Cardiologist',
          type: 'Full-time',
          location: 'Gilgit, Pakistan',
          experience: '5+ years',
          education: 'MBBS, MD Cardiology',
          description: 'We are seeking an experienced cardiologist to lead our cardiology department. The ideal candidate will have extensive experience in diagnosing and treating heart conditions.',
          requirements: [
            'Board certified in Cardiology',
            'Minimum 5 years of clinical experience',
            'Strong diagnostic and surgical skills',
            'Excellent communication skills'
          ],
          salary: 'Competitive Package'
        },
        {
          title: 'Cardiac Nurse',
          type: 'Full-time',
          location: 'Gilgit, Pakistan',
          experience: '2+ years',
          education: 'BSN, RN License',
          description: 'Join our cardiac care nursing team. Provide compassionate care to patients with heart conditions in our state-of-the-art facility.',
          requirements: [
            'Registered Nurse (RN) license',
            'Cardiac care certification preferred',
            '2+ years nursing experience',
            'Strong patient care skills'
          ],
          salary: 'Attractive Package'
        },
        {
          title: 'Cardiology Technician',
          type: 'Full-time',
          location: 'Gilgit, Pakistan',
          experience: '1+ years',
          education: 'Diploma/Certification',
          description: 'Support our cardiology team by operating diagnostic equipment and assisting with cardiac procedures.',
          requirements: [
            'Cardiology technician certification',
            'Experience with ECG, ECHO equipment',
            'Attention to detail',
            'Team player'
          ],
          salary: 'Market Competitive'
        }
      ]
    },
    {
      id: 'neurology',
      name: 'Neurology Department',
      description: 'Work with our neurology specialists in advanced brain care',
      jobs: [
        {
          title: 'Neurologist',
          type: 'Full-time',
          location: 'Gilgit, Pakistan',
          experience: '4+ years',
          education: 'MBBS, MD Neurology',
          description: 'Seeking a skilled neurologist to diagnose and treat disorders of the nervous system. Work with advanced diagnostic tools and provide expert care.',
          requirements: [
            'Board certified in Neurology',
            '4+ years of clinical experience',
            'Experience with EEG, MRI interpretation',
            'Strong analytical skills'
          ],
          salary: 'Competitive Package'
        },
        {
          title: 'Neurology Nurse',
          type: 'Full-time',
          location: 'Gilgit, Pakistan',
          experience: '2+ years',
          education: 'BSN, RN License',
          description: 'Provide specialized nursing care to neurology patients. Monitor neurological status and assist with procedures.',
          requirements: [
            'RN license required',
            'Neurology nursing experience',
            'Strong observation skills',
            'Compassionate patient care'
          ],
          salary: 'Attractive Package'
        }
      ]
    },
    {
      id: 'pediatrics',
      name: 'Pediatrics Department',
      description: 'Help care for children and families in our pediatrics unit',
      jobs: [
        {
          title: 'Pediatrician',
          type: 'Full-time',
          location: 'Gilgit, Pakistan',
          experience: '3+ years',
          education: 'MBBS, MD Pediatrics',
          description: 'Join our pediatrics team to provide comprehensive medical care for infants, children, and adolescents.',
          requirements: [
            'Board certified in Pediatrics',
            '3+ years pediatric experience',
            'Excellent bedside manner',
            'Strong communication with families'
          ],
          salary: 'Competitive Package'
        },
        {
          title: 'Pediatric Nurse',
          type: 'Full-time',
          location: 'Gilgit, Pakistan',
          experience: '1+ years',
          education: 'BSN, RN License',
          description: 'Care for young patients in our child-friendly pediatrics unit. Provide compassionate nursing care to children and support their families.',
          requirements: [
            'RN license required',
            'Pediatric nursing experience preferred',
            'Patience and empathy',
            'Child-friendly approach'
          ],
          salary: 'Attractive Package'
        }
      ]
    },
    {
      id: 'orthopedics',
      name: 'Orthopedics Department',
      description: 'Join our orthopedics team in musculoskeletal care',
      jobs: [
        {
          title: 'Orthopedic Surgeon',
          type: 'Full-time',
          location: 'Gilgit, Pakistan',
          experience: '5+ years',
          education: 'MBBS, MS Orthopedics',
          description: 'Lead orthopedic procedures and treat musculoskeletal conditions. Work with advanced surgical techniques and equipment.',
          requirements: [
            'Board certified in Orthopedics',
            '5+ years surgical experience',
            'Expertise in joint replacement',
            'Strong surgical skills'
          ],
          salary: 'Competitive Package'
        },
        {
          title: 'Physiotherapist',
          type: 'Full-time',
          location: 'Gilgit, Pakistan',
          experience: '2+ years',
          education: 'DPT, License',
          description: 'Help patients recover from injuries and surgeries. Design rehabilitation programs and provide physical therapy services.',
          requirements: [
            'DPT degree and license',
            '2+ years experience',
            'Knowledge of rehabilitation techniques',
            'Patient motivation skills'
          ],
          salary: 'Market Competitive'
        }
      ]
    },
    {
      id: 'emergency',
      name: 'Emergency Department',
      description: 'Be part of our 24/7 emergency care team',
      jobs: [
        {
          title: 'Emergency Physician',
          type: 'Full-time',
          location: 'Gilgit, Pakistan',
          experience: '3+ years',
          education: 'MBBS, FCPS Emergency',
          description: 'Provide critical emergency care in our 24/7 emergency department. Handle trauma cases and medical emergencies.',
          requirements: [
            'FCPS Emergency Medicine',
            '3+ years ER experience',
            'Ability to work under pressure',
            'Quick decision-making skills'
          ],
          salary: 'Competitive Package'
        },
        {
          title: 'Emergency Nurse',
          type: 'Full-time',
          location: 'Gilgit, Pakistan',
          experience: '2+ years',
          education: 'BSN, RN License',
          description: 'Work in our fast-paced emergency department. Provide immediate care to patients in critical conditions.',
          requirements: [
            'RN license required',
            'Emergency nursing experience',
            'Ability to handle stress',
            'Quick response skills'
          ],
          salary: 'Attractive Package'
        },
        {
          title: 'Paramedic',
          type: 'Full-time',
          location: 'Gilgit, Pakistan',
          experience: '1+ years',
          education: 'Paramedic Certification',
          description: 'Provide pre-hospital emergency care. Respond to emergency calls and transport patients safely.',
          requirements: [
            'Paramedic certification',
            'Valid driver\'s license',
            'Physical fitness',
            'Emergency response training'
          ],
          salary: 'Market Competitive'
        }
      ]
    },
    {
      id: 'nursing',
      name: 'Nursing Department',
      description: 'Join our dedicated nursing team providing compassionate care',
      jobs: [
        {
          title: 'Registered Nurse (RN)',
          type: 'Full-time',
          location: 'Gilgit, Pakistan',
          experience: '1+ years',
          education: 'BSN, RN License',
          description: 'Provide comprehensive nursing care across various departments. Join our team of dedicated healthcare professionals.',
          requirements: [
            'RN license required',
            '1+ years nursing experience',
            'Strong clinical skills',
            'Compassionate patient care'
          ],
          salary: 'Attractive Package'
        },
        {
          title: 'Nurse Supervisor',
          type: 'Full-time',
          location: 'Gilgit, Pakistan',
          experience: '5+ years',
          education: 'BSN, MSN preferred',
          description: 'Lead and supervise nursing staff. Ensure quality patient care and manage nursing operations.',
          requirements: [
            'RN license required',
            '5+ years nursing experience',
            'Leadership skills',
            'Management experience preferred'
          ],
          salary: 'Competitive Package'
        },
        {
          title: 'ICU Nurse',
          type: 'Full-time',
          location: 'Gilgit, Pakistan',
          experience: '2+ years',
          education: 'BSN, RN License',
          description: 'Provide critical care nursing in our intensive care unit. Monitor critically ill patients and support recovery.',
          requirements: [
            'RN license required',
            'ICU certification preferred',
            '2+ years ICU experience',
            'Advanced monitoring skills'
          ],
          salary: 'Attractive Package'
        }
      ]
    }
  ];

  const filteredDepartments = selectedDepartment === 'all'
    ? departments
    : departments.filter(dept => dept.id === selectedDepartment);

  return (
    <Base>
      <div className="dark:bg-dark min-h-screen transition-colors duration-200">
        <Helmet><title>Careers | Aman Medical Complex Gilgit</title></Helmet>

        {/* Page Header */}
        <section className="relative py-20 bg-gray-100 dark:bg-gray-800 overflow-hidden">
          {/* Background Image */}
          <div className="absolute inset-0 z-0">
            <img
              src="https://images.unsplash.com/photo-1512678080530-7760d81faba6?auto=format&fit=crop&q=80&w=1920"
              alt="Career Opportunities"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-[#002147]/80 dark:bg-gray-900/90"></div>
          </div>
          <div className="container mx-auto px-4 text-center relative z-10">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Join Our Team</h1>
            <p className="text-white font-medium uppercase tracking-widest">Home / Careers</p>
          </div>
        </section>

        {/* Department Filter */}
        <section className="py-12 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() => setSelectedDepartment('all')}
                className={`px-6 py-3 rounded-lg font-bold transition-all ${selectedDepartment === 'all'
                  ? 'bg-[#007bff] text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
              >
                All Departments
              </button>
              {departments.map((dept) => (
                <button
                  key={dept.id}
                  onClick={() => setSelectedDepartment(dept.id)}
                  className={`px-6 py-3 rounded-lg font-bold transition-all ${selectedDepartment === dept.id
                    ? 'bg-[#007bff] text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                >
                  {dept.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Jobs Listing */}
        <section className="py-16 container mx-auto px-4">
          {filteredDepartments.map((department) => (
            <div key={department.id} className="mb-12">
              {/* Department Header */}
              <div className="mb-6">
                <div className="mb-4">
                  <h2 className="text-2xl md:text-3xl font-black text-[#002147] dark:text-white mb-2">
                    {department.name}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    {department.description}
                  </p>
                </div>
              </div>

              {/* Jobs Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {department.jobs.map((job, index) => (
                  <div
                    key={index}
                    className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-md border border-gray-100 dark:border-gray-700 hover:shadow-lg transition-all group"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-lg font-black text-[#002147] dark:text-white group-hover:text-[#007bff] transition-colors">
                        {job.title}
                      </h3>
                      <span className="bg-[#f0f7ff] text-[#007bff] px-2 py-1 rounded text-[10px] font-bold uppercase">
                        {job.type}
                      </span>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                        <FaMapMarkerAlt className="text-[#007bff] text-xs" />
                        <span className="text-xs font-medium">{job.location}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                        <FaBriefcase className="text-[#007bff] text-xs" />
                        <span className="text-xs font-medium">{job.experience} Experience</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                        <FaGraduationCap className="text-[#007bff] text-xs" />
                        <span className="text-xs font-medium">{job.education}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                        <FaClock className="text-[#007bff] text-xs" />
                        <span className="text-xs font-medium">{job.salary}</span>
                      </div>
                    </div>

                    <p className="text-gray-600 dark:text-gray-400 mb-4 leading-relaxed text-xs">
                      {job.description}
                    </p>

                    <div className="mb-4">
                      <h4 className="text-xs font-bold text-[#002147] dark:text-white mb-2 uppercase">
                        Requirements:
                      </h4>
                      <ul className="space-y-1.5">
                        {job.requirements.map((req, reqIndex) => (
                          <li key={reqIndex} className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                            <FaCheckCircle className="text-[#007bff] mt-0.5 flex-shrink-0 text-xs" />
                            <span>{req}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Link
                      to="/contact"
                      className="group inline-flex items-center gap-2 bg-[#007bff] hover:bg-[#0062cc] text-white px-5 py-2 rounded-full text-xs font-bold transition-all duration-300 hover:scale-105 active:scale-95 shadow-md"
                    >
                      Apply Now <FaArrowRight size={10} className="group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-[#007bff] dark:bg-blue-700">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
              Don't See Your Position?
            </h2>
            <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
              We're always looking for talented individuals to join our team. Send us your resume and we'll keep you in mind for future opportunities.
            </p>
            <Link
              to="/contact"
              className="group inline-flex items-center gap-2 bg-white text-[#007bff] hover:bg-gray-100 px-6 py-3 rounded-full font-bold text-lg transition-all duration-300 hover:scale-105 active:scale-95 shadow-xl"
            >
              Submit General Application <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </section>
      </div>
    </Base>
  );
}

export default Careers;

